import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styles from './AdminModals.module.css';
import detailStyles from './DetailPage.module.css';
import { Button } from '../../components/common/Button/Button';
import { useAdmin } from './AdminContext';
import { projectsAPI } from '../../services/api';
import { useToast } from '../../contexts/ToastContext';

export const ProjectEditPage: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const { projects, companies, refreshData } = useAdmin();

  const project = projects.find((p: any) => (p._id || p.id) === projectId);
  const isNew = !projectId;

  const [formData, setFormData] = useState({
    name: '',
    shortDescription: '',
    description: '',
    location: { address: '', city: '', province: '', postalCode: '' },
    startDate: '',
    endDate: '',
    status: 'planning',
    company: '',
    headerImage: '',
  });
  const [headerImageFile, setHeaderImageFile] = useState<File | null>(null);
  const [headerImagePreview, setHeaderImagePreview] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [projectDocuments, setProjectDocuments] = useState<File[]>([]);

  useEffect(() => {
    if (project) {
      setFormData({
        name: project.name,
        shortDescription: (project as any).shortDescription || '',
        description: project.description || '',
        location: typeof project.location === 'string'
          ? { address: '', city: project.location, province: '', postalCode: '' }
          : (project as any).location || { address: '', city: '', province: '', postalCode: '' },
        startDate: project.startDate ? new Date(project.startDate).toISOString().split('T')[0] : '',
        endDate: project.endDate ? new Date(project.endDate).toISOString().split('T')[0] : '',
        status: project.status as string || 'planning',
        company: (project as any).company?._id || (project as any).company || '',
        headerImage: (project as any).headerImage || '',
      });
      if ((project as any).headerImage) {
        setHeaderImagePreview((project as any).headerImage);
      }
    }
  }, [project]);

  const handleHeaderImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        toast.warning('Afbeelding te groot', 'Max 10MB.');
        return;
      }
      setHeaderImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => setHeaderImagePreview(reader.result as string);
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveHeaderImage = async () => {
    if (projectId && (project as any)?.headerImage) {
      try {
        await projectsAPI.deleteHeaderImage(projectId);
        toast.success('Header afbeelding verwijderd');
      } catch (error) {
        toast.error('Verwijderen mislukt');
      }
    }
    setHeaderImageFile(null);
    setHeaderImagePreview(null);
    setFormData({ ...formData, headerImage: '' });
  };

  const handleDocumentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;
    const validFiles: File[] = [];
    const validTypes = ['.pdf', '.doc', '.docx', '.txt', '.ppt', '.pptx'];
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      if (file.size > 50 * 1024 * 1024) {
        toast.warning('Bestand te groot', `"${file.name}" is groter dan 50MB.`);
        continue;
      }
      const ext = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
      if (!validTypes.includes(ext)) {
        toast.warning('Ongeldig formaat', `"${file.name}" wordt niet ondersteund.`);
        continue;
      }
      validFiles.push(file);
    }
    if (validFiles.length > 0) {
      setProjectDocuments(prev => [...prev, ...validFiles]);
    }
  };

  const buildPayload = () => {
    const { headerImage, ...rest } = formData;
    return { ...rest };
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);

    try {
      const payload = buildPayload();
      if (isNew) {
        // Documenten worden als CONTEXT opgeslagen (voor o.a. Fase 1), niet automatisch
        // omgezet in persona's/strategie — dat gebeurt fase-voor-fase met De Lock.
        const created = await projectsAPI.create(payload);
        const createdId = created._id || created.id;
        if (createdId && projectDocuments.length > 0) {
          try { await projectsAPI.uploadDocuments(createdId, projectDocuments); } catch {}
        }
        if (headerImageFile && createdId) {
          try { await projectsAPI.uploadHeaderImage(createdId, headerImageFile); } catch {}
        }
        toast.success('Project aangemaakt');
        refreshData();
        navigate(`/admin/projects/${createdId}`);
      } else {
        await projectsAPI.update(projectId!, payload);
        if (headerImageFile) {
          try { await projectsAPI.uploadHeaderImage(projectId!, headerImageFile); } catch {}
        }
        toast.success('Project bijgewerkt');
        refreshData();
        navigate(`/admin/projects/${projectId}`);
      }
    } catch (error: any) {
      toast.error('Opslaan mislukt', error.response?.data?.error || 'Er ging iets mis.');
    } finally {
      setSaving(false);
    }
  };

  if (projectId && !project) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <p style={{ color: '#9DA6A8', marginBottom: '1rem' }}>Project niet gevonden.</p>
        <Button onClick={() => navigate('/admin/projects')}>Terug naar projecten</Button>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '800px' }}>
      <button onClick={() => navigate(projectId ? `/admin/projects/${projectId}` : '/admin/projects')} className={detailStyles.backLink}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="15 18 9 12 15 6" /></svg>
        {projectId ? 'Terug naar project' : 'Terug naar projecten'}
      </button>

      <h1 style={{ fontFamily: "'Avenir Next', sans-serif", fontSize: '1.75rem', fontWeight: 600, color: '#323232', margin: '0 0 1.5rem' }}>
        {isNew ? 'Nieuw project' : `${project?.name} bewerken`}
      </h1>

      <div style={{ background: '#FFFFFF', borderRadius: '12px', border: '1px solid #EEEEEE', padding: '2rem' }}>
        <form onSubmit={handleSubmit} className={styles.form}>
          <div className={styles.formGroup}>
            <label htmlFor="name">Projectnaam *</label>
            <input type="text" id="name" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required />
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="shortDescription">Korte beschrijving (hero-tekst)</label>
            <textarea id="shortDescription" value={formData.shortDescription} onChange={(e) => setFormData({ ...formData, shortDescription: e.target.value })} rows={3} placeholder="Korte introductietekst die getoond wordt in de hero-banner op het dashboard (max 2-3 zinnen)." />
            <small style={{ color: '#9DA6A8', fontSize: '0.8rem' }}>Wordt getoond in de header-afbeelding op het dashboard.</small>
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="description">Uitgebreide beschrijving</label>
            <textarea id="description" value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} rows={8} placeholder="Volledige projectbeschrijving met achtergrond, doelstellingen en context. Wordt getoond in de sectie 'Over dit project'." />
            <small style={{ color: '#9DA6A8', fontSize: '0.8rem' }}>Wordt getoond onder 'Over dit project' op het dashboard en op de projectwebsite.</small>
          </div>

          <div className={styles.formGroup}>
            <label>Header afbeelding</label>
            <div className={styles.logoUploadContainer}>
              {headerImagePreview ? (
                <div className={styles.logoPreview} style={{ width: '100%', height: 'auto', aspectRatio: '21 / 9' }}>
                  <img src={headerImagePreview} alt="Header preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  <button type="button" onClick={handleRemoveHeaderImage} className={styles.removeLogoBtn} title="Verwijder">×</button>
                </div>
              ) : (
                <div className={styles.logoPlaceholder} style={{ width: '100%', height: 'auto', aspectRatio: '21 / 9' }}>
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#9DA6A8" strokeWidth="1.5">
                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2" /><circle cx="8.5" cy="8.5" r="1.5" /><polyline points="21 15 16 10 5 21" />
                  </svg>
                  <span>Geen header afbeelding</span>
                </div>
              )}
              <input type="file" id="headerImage" accept="image/*" onChange={handleHeaderImageChange} className={styles.fileInput} />
              <label htmlFor="headerImage" className={styles.uploadButton}>Kies afbeelding</label>
              <small>Max 10MB. JPEG, PNG, GIF, SVG, WebP. Je hoeft de foto niet zelf bij te snijden: hij wordt automatisch passend gemaakt voor de brede kop van het project (het midden blijft zichtbaar), zoals hierboven.</small>
            </div>
          </div>

          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label htmlFor="city">Stad *</label>
              <input type="text" id="city" value={formData.location.city} onChange={(e) => setFormData({ ...formData, location: { ...formData.location, city: e.target.value } })} required />
            </div>
            <div className={styles.formGroup}>
              <label htmlFor="province">Provincie</label>
              <input type="text" id="province" value={formData.location.province} onChange={(e) => setFormData({ ...formData, location: { ...formData.location, province: e.target.value } })} />
            </div>
          </div>

          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label htmlFor="address">Adres</label>
              <input type="text" id="address" value={formData.location.address} onChange={(e) => setFormData({ ...formData, location: { ...formData.location, address: e.target.value } })} />
            </div>
            <div className={styles.formGroup}>
              <label htmlFor="postalCode">Postcode</label>
              <input type="text" id="postalCode" value={formData.location.postalCode} onChange={(e) => setFormData({ ...formData, location: { ...formData.location, postalCode: e.target.value } })} />
            </div>
          </div>

          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label htmlFor="startDate">Startdatum</label>
              <input type="date" id="startDate" value={formData.startDate} onChange={(e) => setFormData({ ...formData, startDate: e.target.value })} />
            </div>
            <div className={styles.formGroup}>
              <label htmlFor="endDate">Einddatum</label>
              <input type="date" id="endDate" value={formData.endDate} onChange={(e) => setFormData({ ...formData, endDate: e.target.value })} />
            </div>
          </div>

          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label htmlFor="status">Status *</label>
              <select id="status" value={formData.status} onChange={(e) => setFormData({ ...formData, status: e.target.value })} required>
                <option value="planning">In afwachting (nog niet gestart)</option>
                <option value="active">Actief</option>
                <option value="paused">Gepauzeerd</option>
                <option value="completed">Afgerond</option>
                <option value="cancelled">Geannuleerd</option>
              </select>
            </div>
            <div className={styles.formGroup}>
              <label htmlFor="company">Klant</label>
              <select id="company" value={formData.company} onChange={(e) => setFormData({ ...formData, company: e.target.value })}>
                <option value="">Selecteer klant</option>
                {companies.map((c) => <option key={c._id} value={c._id}>{c.name}</option>)}
              </select>
            </div>
          </div>

          {isNew && (
            <div className={styles.formGroup}>
              <label>Projectdocumenten (optioneel)</label>
              <div className={styles.documentUploadContainer}>
                {projectDocuments.length > 0 ? (
                  <div className={styles.documentsList}>
                    {projectDocuments.map((doc, i) => (
                      <div key={i} className={styles.documentItem}>
                        <div className={styles.documentName}>
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#1E3A2F" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /></svg>
                          <span>{doc.name}</span>
                          <button type="button" onClick={() => setProjectDocuments(prev => prev.filter((_, idx) => idx !== i))} className={styles.removeDocBtn}>×</button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className={styles.documentPlaceholder}>
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#9DA6A8" strokeWidth="1.5"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /></svg>
                    <span>Voeg documenten toe die de AI als achtergrond gebruikt, bijvoorbeeld een stakeholderlijst, projectplanning, werkplanning of informatie over de omgeving. De AI vult niets automatisch in.</span>
                  </div>
                )}
                <input type="file" id="projectDocuments" accept=".pdf,.doc,.docx,.txt,.ppt,.pptx" onChange={handleDocumentChange} className={styles.fileInput} multiple />
                <label htmlFor="projectDocuments" className={styles.uploadButton}>{projectDocuments.length > 0 ? 'Meer documenten' : 'Kies documenten'}</label>
                <small>PDF, Word, PowerPoint of TXT. Max 50MB per bestand.</small>
              </div>
            </div>
          )}

          <div className={styles.formActions}>
            <Button type="button" variant="secondary" onClick={() => navigate(projectId ? `/admin/projects/${projectId}` : '/admin/projects')}>
              Annuleren
            </Button>
            <Button type="submit" variant="primary" disabled={saving}>
              {saving ? 'Opslaan...' : (isNew ? 'Project aanmaken' : 'Opslaan')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};
